import { ArrowUp, type LucideIcon } from 'lucide-react';

// =============================================================================
// positioning-labels.ts — Habillage F-10 UNAIRE : drapeau ORANGE doublé libellé + icône.
// =============================================================================
// RÉVISION UNAIRE (G0-S17-16, WCAG P-06 / ENF-06 / ST-NFR-03) : l'indicateur F-10 est désormais
// UNAIRE — UNE seule couleur ORANGE quand le TJM saisi est ≥ P75 (« au-dessus du marché »). Le
// VERT et le BLEU sont SUPPRIMÉS côté fournisseur ; le tooltip de bornes P25/P75 est SUPPRIMÉ
// (le front reçoit un drapeau seul, il ne dérive plus rien). La COULEUR n'est JAMAIS le seul
// porteur d'information : l'état orange rend un LIBELLÉ TEXTE **et** une ICÔNE distincte ; la
// couleur (classe Tailwind sur token tonal) n'est qu'un RENFORT visuel. Un utilisateur daltonien
// (ou en niveaux de gris) lit « Au-dessus du marché » + la flèche, indépendamment de la couleur.
// jest-axe 0 violation est le gate automatisé ; le doublement libellé/icône est le gate éditorial.
//
// Code UNAIRE F-10 (G0-S17-16, DBU §3.6) — VOLONTAIREMENT distinct du code Admin (Hors Jeux sur médiane du couple §2.5 v2.7,
// vue opérationnelle S14, INCHANGÉE). Échelle de spacing/couleur Tailwind uniquement (pas de magic
// number). DS sweep Cat 2 : teinte portée par le token `--chart-3` (recette AA-SAFE — texte
// `text-foreground` sur tint translucide, tone via la bordure ; contraste texte/fond ≥ 4.5:1).

/** Forme d'habillage de l'unique état affiché (orange) : libellé FR + icône + classes + description. */
export interface PositioningPresentation {
  /** Libellé court affiché à côté de l'icône (jamais la couleur seule). */
  readonly label: string;
  /** Icône lucide (renfort non chromatique). */
  readonly Icon: LucideIcon;
  /** Classes Tailwind du badge (fond tonal + texte contrasté + bordure). */
  readonly badgeClassName: string;
  /** Description longue pour lecteur d'écran / légende (sr-only + tooltip légende). */
  readonly description: string;
}

/**
 * Habillage de l'UNIQUE état affiché F-10 (G0-S17-16) : ORANGE = « Au-dessus du marché » (TJM ≥ P75).
 * Quand l'indicateur n'est PAS orange (et non masqué), RIEN n'est affiché : il n'existe donc pas
 * d'autre entrée d'habillage. Le libellé + l'icône portent l'information — la couleur n'est jamais seule.
 */
export const POSITIONING_ORANGE_PRESENTATION: PositioningPresentation = {
  label: 'Au-dessus du marché',
  Icon: ArrowUp,
  // DS sweep Cat 2 : palette ambre brute retirée au profit du token DS `--chart-3`
  // (teinte ambre conservée, hue-fidèle). Recette AA-SAFE : fond = tint translucide,
  // texte = `text-foreground` (jamais le tone sur le texte), tone via la bordure. Reste
  // un indicateur FOURNISSEUR distinct — surtout PAS un code métier (`--metier-*`
  // admin-only). Le LIBELLÉ + l'icône portent l'information (WCAG 1.4.1).
  badgeClassName: 'border-chart-3/40 bg-chart-3/10 text-foreground',
  description:
    'Au-dessus du marché : votre tarif se situe dans le haut de la fourchette du marché anonymisé.',
};

/** Libellé NEUTRE affiché quand l'indicateur est MASQUÉ (effectif sous le seuil d'anonymat). */
export const POSITIONING_MASKED_LABEL = 'Indicateur indisponible';

/** Infobulle native du cas masqué : explique l'absence d'indicateur SANS rien laisser fuir (G0-S17-3). */
export const POSITIONING_MASKED_HINT =
  'Indicateur indisponible : pas assez de données pour préserver l’anonymat du marché.';
